import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import VirtualAssistant from './components/VirtualAssistant';
import './App.css';

// Import pages
import ClientDashboard from './pages/client/Dashboard';
import AdminDashboard from './pages/admin/Dashboard';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import AdminLogin from './pages/admin/Login';
import AdminSignup from './pages/admin/Signup';
import ClientAppointments from './pages/client/Appointments';
import ClientEstimates from './pages/client/Estimates';
import ClientProfile from './pages/client/Profile';
import ClientSupportTickets from './pages/client/SupportTickets';
import ClientNotifications from './pages/client/Notifications';
import AdminAppointments from './pages/admin/Appointments';
import AdminUsers from './pages/admin/Users';
import AdminSupportTickets from './pages/admin/SupportTickets';
import AdminProfile from './pages/admin/Profile';
import AdminTreeHealth from './pages/admin/TreeHealth';
import AdminSeasonalPlanning from './pages/admin/SeasonalPlanning';
import AdminAnalytics from './pages/admin/Analytics';
import AdminMessages from './pages/admin/Messages';
import AdminNotifications from './pages/admin/Notifications';
import UserManagement from './pages/admin/UserManagement';
import ClientMessages from './pages/client/Messages';
import SubmitReview from './pages/client/SubmitReview';
import MyReviews from './pages/client/MyReviews';
import AdminSlotManagement from './pages/admin/SlotManagement';
import AdminEstimates from './pages/admin/Estimates';
import AdminNotificationTest from './pages/admin/NotificationTest';

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { currentUser, userRole } = useAuth();
  
  if (!currentUser) {
    // Redirect to appropriate login page based on required role
    return <Navigate to={requiredRole === 'admin' ? '/admin/login' : '/login'} replace />;
  }
  
  if (requiredRole && userRole !== requiredRole) {
    return <Navigate to={userRole === 'admin' ? '/admin' : '/'} replace />;
  }
  
  return children;
};

// App Routes Component
const AppRoutes = () => {
  const { currentUser, userRole } = useAuth();

  return (
    <Routes>
      {/* Client Auth Routes */}
      <Route 
        path="/login" 
        element={currentUser ? <Navigate to={userRole === 'admin' ? '/admin' : '/'} replace /> : <Login />} 
      />
      <Route 
        path="/signup" 
        element={currentUser ? <Navigate to={userRole === 'admin' ? '/admin' : '/'} replace /> : <Signup />} 
      />
      
      {/* Admin Auth Routes */}
      <Route 
        path="/admin/login" 
        element={currentUser ? <Navigate to={userRole === 'admin' ? '/admin' : '/'} replace /> : <AdminLogin />} 
      />
      <Route 
        path="/admin/signup" 
        element={currentUser ? <Navigate to={userRole === 'admin' ? '/admin' : '/'} replace /> : <AdminSignup />} 
      />
      
      {/* Client Routes */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute requiredRole="client">
            <ClientDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/appointments" 
        element={
          <ProtectedRoute requiredRole="client">
            <ClientAppointments />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/estimates" 
        element={
          <ProtectedRoute requiredRole="client">
            <ClientEstimates />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute requiredRole="client">
            <ClientProfile />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/support" 
        element={
          <ProtectedRoute requiredRole="client">
            <ClientSupportTickets />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/notifications" 
        element={
          <ProtectedRoute requiredRole="client">
            <ClientNotifications />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/messages" 
        element={
          <ProtectedRoute requiredRole="client">
            <ClientMessages />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/submit-review" 
        element={
          <ProtectedRoute requiredRole="client">
            <SubmitReview />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/my-reviews" 
        element={
          <ProtectedRoute requiredRole="client">
            <MyReviews />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Routes */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/appointments" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminAppointments />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/slots" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminSlotManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/estimates" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminEstimates />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/users" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminUsers />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/support" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminSupportTickets />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/profile" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminProfile />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/tree-health" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminTreeHealth />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/seasonal-planning" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminSeasonalPlanning />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/analytics" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminAnalytics />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/messages" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminMessages />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/notifications" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminNotifications />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/manage-admins" 
        element={
          <ProtectedRoute requiredRole="admin">
            <UserManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/notification-test" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminNotificationTest />
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback Route */}
      <Route 
        path="*" 
        element={<Navigate to={currentUser ? (userRole === 'admin' ? '/admin' : '/') : '/login'} replace />} 
      />
    </Routes>
  );
};

function App() {
  return (
    <div className="app">
      <AuthProvider>
        <NotificationProvider>
          <Router>
            <AppRoutes />
            <VirtualAssistant />
          </Router>
        </NotificationProvider>
      </AuthProvider>
    </div>
  );
}

export default App;